<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { CaldavAccount } from '@/api'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { useAccountsEditor } from '@/composables/useAccountsEditor'
import { reportError } from '@/errorReport'
import { accountsPath } from '@/settings/accountsNav'

// The add form (no `account`) and the edit form (with one), the same fields. A blank password on edit keeps the stored one.
const props = defineProps<{ account?: CaldavAccount }>()
const { t } = useI18n()
const router = useRouter()
const editor = useAccountsEditor()

const form = reactive({
  name: props.account?.name ?? '',
  url: props.account?.url ?? '',
  readonly: props.account?.readonly ?? false,
  username: props.account?.username ?? '',
  password: '',
})
const submitting = ref(false)
const error = ref('')

async function submit(): Promise<void> {
  error.value = ''
  submitting.value = true
  try {
    if (props.account) {
      await editor.update(props.account.id, {
        name: form.name,
        url: form.url,
        readonly: form.readonly,
        username: form.username || null,
        // Only send a password if a new one was typed -- blank keeps the stored (encrypted) one instead of clearing it.
        ...(form.password ? { password: form.password } : {}),
      })
      await router.replace(accountsPath(props.account.id))
    } else {
      const created = await editor.create({
        name: form.name,
        url: form.url,
        readonly: form.readonly,
        username: form.username || null,
        password: form.password || null,
      })
      await router.replace(accountsPath(created.id))
    }
  } catch (e) {
    const key = props.account ? 'accounts.err.save' : 'accounts.err.create'
    reportError(e, key)
    error.value = editor.messageOf(e, key)
  } finally {
    submitting.value = false
  }
}

function cancel(): void {
  void router.replace(props.account ? accountsPath(props.account.id) : accountsPath())
}
</script>

<template>
  <form class="account-form" @submit.prevent="submit">
    <UiGroup>
      <UiList>
        <UiRow as="label" :label="t('common.name')">
          <input v-model="form.name" class="ui-input" required :placeholder="t('accounts.namePlaceholder')" :aria-label="t('common.name')" />
        </UiRow>
        <UiRow as="label" :label="t('detail.field.url')">
          <input
            v-model="form.url"
            class="ui-input"
            required
            type="url"
            placeholder="https://caldav.example.com/..."
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            :aria-label="t('detail.field.url')"
          />
        </UiRow>
        <UiRow as="label" :label="t('common.username')">
          <input
            v-model="form.username"
            class="ui-input"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            :placeholder="t('accounts.optional')"
            :aria-label="t('common.username')"
          />
        </UiRow>
        <UiRow as="label" :label="t('common.password')">
          <input
            v-model="form.password"
            class="ui-input"
            type="password"
            autocomplete="new-password"
            :placeholder="account ? t('accounts.keepPassword') : t('accounts.optional')"
            :aria-label="t('common.password')"
          />
        </UiRow>
        <UiRow as="label">
          <input v-model="form.readonly" type="checkbox" />
          <span>{{ account ? t('accounts.readonly') : t('accounts.readonlyLong') }}</span>
        </UiRow>
      </UiList>
      <template #footer>
        <p v-if="error" class="error" role="alert">{{ error }}</p>
      </template>
    </UiGroup>

    <UiGroup>
      <UiList>
        <UiRow as="button" type="submit" class="action" :disabled="submitting" data-testid="save-account">
          {{ submitting ? (account ? t('accounts.saving') : t('accounts.adding')) : account ? t('common.save') : t('accounts.addTitle') }}
        </UiRow>
        <UiRow as="button" type="button" class="action" :disabled="submitting" @click="cancel">
          {{ t('common.cancel') }}
        </UiRow>
      </UiList>
    </UiGroup>
  </form>
</template>

<style scoped>
.action {
  width: 100%;
  border: 0;
  background: none;
  color: var(--tg-link-color);
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.action:disabled {
  opacity: 0.6;
  cursor: default;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
